import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { BUILTIN_STATUS_GROUPS } from '@/lib/statusRegistry'
import type { StatusDef } from '@/lib/statusRegistry'
import { filterStatusGroups } from '@/lib/statusFilter'
import { statusIconName, statusIconSvg, STATUS_ICON_MINT } from '@/render/statusIcons'

// Chip grid geometry — fixed px tracks, not `1fr`, so the grid's width is
// decoupled from whatever the scrollable wrapper happens to have available.
// That's what the 172px version got wrong: 1fr columns just divided up
// whatever width overflow-y's scrollbar left behind, so the 4th column
// clipped whenever the (41-status, 200px-capped) list actually scrolled.
const CHIP_PX = 34
const GAP_PX = 4
const COLS = 4
const GRID_WIDTH = COLS * CHIP_PX + (COLS - 1) * GAP_PX // 148

// Panel width — real box model, not a guess. Tailwind's preflight puts every
// box in border-box, so this total already includes border + padding (no
// separate content-box math to worry about):
//   .ac-hud-card border                 1px × 2 sides =  2
//   inline padding (0.5rem)             8px × 2 sides = 16
//   chip grid (fixed columns, above)                  = GRID_WIDTH (148)
//   scrollbar-gutter: stable reserve on the list       = 18  (covers a
//     classic ~15-17px scrollbar with a couple px of slack; the grid's
//     columns are fixed px rather than `1fr` specifically so this reserve
//     can't pinch the 4th column the way it did at 172px)
const SCROLLBAR_RESERVE_PX = 18
const PANEL_CHROME_PX = 2 + 16 // border + padding
const PANEL_WIDTH = PANEL_CHROME_PX + GRID_WIDTH + SCROLLBAR_RESERVE_PX // 184

/**
 * Chip inner icon: image art when the status has iconUrl, else the mint SVG
 * glyph (unchanged legacy treatment for keys without art, e.g. prone).
 */
export function StatusChipIcon({ status, size = 18 }: { status: StatusDef; size?: number }) {
  if (status.iconUrl) {
    return (
      <img
        src={status.iconUrl}
        alt=""
        aria-hidden
        draggable={false}
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
      />
    )
  }
  return (
    <span
      aria-hidden
      style={{ display: 'flex', width: size, height: size }}
      dangerouslySetInnerHTML={{
        __html: statusIconSvg(statusIconName(status.key), STATUS_ICON_MINT, size),
      }}
    />
  )
}

interface StatusMenuProps {
  /** Fixed-position anchor for the popover (screen coords). */
  position: { x: number; y: number }
  /** Currently active status keys on this token. */
  activeStatuses: string[]
  /** Toggle a single condition on/off. Menu stays open after each toggle. */
  onToggle: (key: string) => void
  /** Remove all conditions from the token. Menu stays open. */
  onClearAll: () => void
  /** Close the menu (called on outside-click). */
  onClose: () => void
  /**
   * Ref to the element that triggered the menu.  Included in the "inside"
   * zone so that clicking the trigger while the menu is open is handled by
   * the trigger's own onClick (toggle), not by the outside-click handler
   * (close → immediate reopen flicker).
   */
  triggerRef: RefObject<HTMLElement>
}

/**
 * Floating condition-picker popover for token status management.
 *
 * Renders a fixed-position `.ac-hud-card` panel with:
 *   • Grouped sections (Conditions / Numbers / Colors / Symbols), each a
 *     4-column grid of status chips (icon + tooltip). Chips with image art
 *     (`iconUrl`) render the PNG; others keep the mint SVG glyph.
 *   • Active chips are highlighted with the GM-gold tint.
 *   • A search box filters the grouped sections by name/key as you type;
 *     an empty result renders a muted "No matching statuses" line instead
 *     of a blank panel. Typing never autofocuses or closes the menu.
 *   • "Clear All" sits next to the search box, styled as a filled button
 *     (it used to be an easy-to-miss muted text link in the header).
 *   • Height-capped at 200px with overflow-y: auto so a long list scrolls
 *     inside the panel rather than growing unbounded.
 *   • Multi-select: clicking a chip toggles it without closing the menu.
 *   • Closes on outside-click using the press-AND-release pattern — both
 *     pointerdown AND pointerup must land outside the menu (and outside the
 *     trigger) before onClose fires.  A bare `click` listener would close the
 *     menu immediately on an inner-chip re-render (the React reconciler
 *     recreates the clicked node before the click event bubbles to document,
 *     making `contains()` return false for a genuinely inside click).
 */
export function StatusMenu({
  position,
  activeStatuses,
  onToggle,
  onClearAll,
  onClose,
  triggerRef,
}: StatusMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const pointerDownOutside = useRef(false)
  const [search, setSearch] = useState('')
  const visibleGroups = filterStatusGroups(BUILTIN_STATUS_GROUPS, search)

  // Keep a stable ref to onClose so the effect doesn't re-subscribe every
  // render (onClose is an inline arrow in the parent).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const isInside = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node)) return false
      return !!(
        menuRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      )
    }

    const onPointerDown = (e: PointerEvent) => {
      pointerDownOutside.current = !isInside(e.target)
    }
    const onPointerUp = (e: PointerEvent) => {
      if (pointerDownOutside.current && !isInside(e.target)) {
        onCloseRef.current()
      }
      pointerDownOutside.current = false
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('pointerup', onPointerUp, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('pointerup', onPointerUp, true)
    }
    // triggerRef identity is stable (same useRef object across renders).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      ref={menuRef}
      className="ac-hud-card"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: PANEL_WIDTH,
        padding: '0.5rem',
        zIndex: 300,
        pointerEvents: 'auto',
      }}
      // Prevent right-clicks on the menu from opening the canvas context menu.
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {/* ── Header: label only ── */}
      <div style={{ marginBottom: '0.375rem' }}>
        <span
          style={{
            fontSize: '0.625rem',
            color: 'hsl(var(--ac-text-muted))',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          Statuses
        </span>
      </div>

      {/* ── Search + Clear All row. Clear All was an invisible top-right text
          link before; it's relocated here (not duplicated) and given a
          filled background so it's actually findable. No autoFocus on the
          input — this popover opens from a canvas token click, and stealing
          focus would break that interaction. ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          marginBottom: '0.375rem',
        }}
      >
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          aria-label="Search statuses"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: '0.6875rem',
            padding: '0.25rem 0.375rem',
            borderRadius: 4,
            border: '1px solid hsl(var(--ac-dark-accent))',
            background: 'hsl(var(--ac-dark-primary) / 0.5)',
            color: 'hsl(var(--ac-text-light, 0 0% 98%))',
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={onClearAll}
          aria-label="Clear all conditions"
          style={{
            flexShrink: 0,
            whiteSpace: 'nowrap',
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'hsl(var(--ac-text-light, 0 0% 98%))',
            background: 'hsl(var(--ac-dark-accent))',
            border: '1px solid hsl(var(--ac-dark-accent))',
            borderRadius: 4,
            cursor: 'pointer',
            padding: '0.25rem 0.5rem',
            lineHeight: 1,
          }}
        >
          Clear All
        </button>
      </div>

      {/* ── Grouped sections (Conditions / Numbers / Colors / Symbols), each a
          4-column chip grid; one shared 200px scroll cap over all sections.
          scrollbarGutter: 'stable' reserves the scrollbar's space up front
          instead of only when it actually renders, so the grid's fixed
          width never gets pinched by a scrollbar that appears mid-filter. ── */}
      <div style={{ maxHeight: 200, overflowY: 'auto', scrollbarGutter: 'stable' }}>
        {visibleGroups.length === 0 ? (
          <div
            style={{
              fontSize: '0.6875rem',
              color: 'hsl(var(--ac-text-muted))',
              padding: '0.5rem 0',
              textAlign: 'center',
            }}
          >
            No matching statuses
          </div>
        ) : (
          visibleGroups.map((g, gi) => (
            <div key={g.group}>
              <div
                style={{
                  fontSize: '0.5625rem',
                  color: 'hsl(var(--ac-text-muted))',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  margin: gi === 0 ? '0 0 0.25rem' : '0.375rem 0 0.25rem',
                }}
              >
                {g.label}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${COLS}, ${CHIP_PX}px)`,
                  gap: GAP_PX,
                }}
              >
                {g.statuses.map((s) => {
                  const active = activeStatuses.includes(s.key)
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => onToggle(s.key)}
                      title={s.name}
                      aria-label={s.name}
                      aria-pressed={active}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        aspectRatio: '1',
                        fontSize: '1.0625rem',
                        lineHeight: 1,
                        padding: 0,
                        borderRadius: 6,
                        border: active
                          ? '1.5px solid hsl(var(--ac-gm-gold, 45 80% 50%) / 0.65)'
                          : '1px solid hsl(var(--ac-dark-accent))',
                        background: active
                          ? 'hsl(var(--ac-gm-gold, 45 80% 50%) / 0.22)'
                          : 'hsl(var(--ac-dark-primary) / 0.5)',
                        cursor: 'pointer',
                        transition: 'background 80ms, border-color 80ms',
                      }}
                    >
                      <StatusChipIcon status={s} />
                    </button>
                  )
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
